/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import { GraphQLResponseError } from '@jira/polaris__lib-relay-fetch/src/GraphQLResponseError.tsx';
import { RelayNetworkError } from '@jira/polaris__lib-relay-fetch/src/createRelayNetworkError.tsx';
import ExtensibleError from '@jira/platform__utils-extensible-error/src/index.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { defineMessage, type MessageDescriptor } from '@atlassian/jira-intl';
import { ENTITY_LIMIT_ERROR_TYPE, type LimitType } from './LimitType.types.tsx';

type EntityLimitErrorTypeKeys = keyof typeof ENTITY_LIMIT_ERROR_TYPE;

type GraphQLErrorLike = {
	message: string;
	extensions?: { statusCode?: number; limitType?: EntityLimitErrorTypeKeys };
};

const commentsPerViewMessage = defineMessage({
	id: 'polaris-lib-errors.controllers.entity-limit-error.comments-per-view',
	defaultMessage:
		'We couldn’t create a new <item>comment</item> because this <item>view</item> has reached its limit for <item>comments</item>. Remove <item>comment</item> to create a new <item>comment</item>',
	description: 'Flag description for entity limit error for comments per view',
});

const fieldsPerViewMessage = defineMessage({
	id: 'polaris-lib-errors.controllers.entity-limit-error.fields-per-view',
	defaultMessage:
		'We couldn’t create a new <item>field</item> because this <item>view</item> has reached its limit for <item>fields</item>. Remove <item>field</item> to create a new <item>field</item>',
	description: 'Flag description for entity limit error for fields per view',
});

const fieldsPerProjectMessage = defineMessage({
	id: 'polaris-lib-errors.controllers.entity-limit-error.fields-per-project',
	defaultMessage:
		'We couldn’t create a new <item>field</item> because this <item>project</item> has reached its limit for <item>fields</item>. Remove <item>field</item> to create a new <item>field</item>',
	description: 'Flag description for entity limit error for fields per project',
});

const insightsPerIdeaMessage = defineMessage({
	id: 'polaris-lib-errors.controllers.entity-limit-error.insights-per-idea',
	defaultMessage:
		'We couldn’t create a new <item>insight</item> because this <item>idea</item> has reached its limit for <item>insights</item>. Remove <item>insight</item> to create a new <item>insight</item>',
	description: 'Flag description for entity limit error for insights per idea',
});

const insightsPerProjectMessage = defineMessage({
	id: 'polaris-lib-errors.controllers.entity-limit-error.insights-per-project',
	defaultMessage:
		'We couldn’t create a new <item>insight</item> because this <item>project</item> has reached its limit for <item>insights</item>. Remove <item>insight</item> to create a new <item>insight</item>',
	description: 'Flag description for entity limit error for insights per project',
});

const labelsPerSnippetMessage = defineMessage({
	id: 'polaris-lib-errors.controllers.entity-limit-error.labels-per-snippet',
	defaultMessage:
		'We couldn’t create a new <item>label</item> because this <item>snippet</item> has reached its limit for <item>labels</item>. Remove <item>label</item> to create a new <item>label</item>',
	description: 'Flag description for entity limit error for labels per snippet',
});

const optionsPerFieldMessage = defineMessage({
	id: 'polaris-lib-errors.controllers.entity-limit-error.options-per-field',
	defaultMessage:
		'We couldn’t create a new <item>option</item> because this <item>field</item> has reached its limit for <item>options</item>. Remove <item>option</item> to create a new <item>option</item>',
	description: 'Flag description for entity limit error for options per field',
});

const playContributionsPerPlayMessage = defineMessage({
	id: 'polaris-lib-errors.controllers.entity-limit-error.play-contributions-per-play',
	defaultMessage:
		'We couldn’t create a new <item>vote contribution</item> because this <item>vote</item> has reached its limit for <item>vote contributions</item>. Remove <item>vote contribution</item> to create a new <item>vote contribution</item>',
	description: 'Flag description for entity limit error for play contributions per play',
});

const playContributionsPerProjectMessage = defineMessage({
	id: 'polaris-lib-errors.controllers.entity-limit-error.play-contributions-per-project',
	defaultMessage:
		'We couldn’t create a new <item>vote contribution</item> because this <item>project</item> has reached its limit for <item>vote contributions</item>. Remove <item>vote contribution</item> to create a new <item>vote contribution</item>',
	description: 'Flag description for entity limit error for play contributions per project',
});

const playsPerProjectMessage = defineMessage({
	id: 'polaris-lib-errors.controllers.entity-limit-error.plays-per-project',
	defaultMessage:
		'We couldn’t create a new <item>vote</item> because this <item>project</item> has reached its limit for <item>votes</item>. Remove <item>vote</item> to create a new <item>vote</item>',
	description: 'Flag description for entity limit error for plays per project',
});

const snippetsPerInsightMessage = defineMessage({
	id: 'polaris-lib-errors.controllers.entity-limit-error.snippets-per-insight',
	defaultMessage:
		'We couldn’t create a new <item>snippet</item> because this <item>insight</item> has reached its limit for <item>snippets</item>. Remove <item>snippet</item> to create a new <item>snippet</item>',
	description: 'Flag description for entity limit error for snippets per insight',
});

const viewsPerProjectMessage = defineMessage({
	id: 'polaris-lib-errors.controllers.entity-limit-error.views-per-project',
	defaultMessage:
		'We couldn’t create a new <item>view</item> because this <item>project</item> has reached its limit for <item>views</item>. Remove <item>view</item> to create a new <item>view</item>',
	description: 'Flag description for entity limit error for views per project',
});

const viewsPerCollectionMessage = defineMessage({
	id: 'polaris-lib-errors.controllers.entity-limit-error.views-per-collection',
	defaultMessage:
		'We couldn’t create a new <item>view</item> because this <item>roadmap</item> has reached its limit for <item>views</item>. Remove <item>view</item> to create a new <item>view</item>',
	description: 'Flag description for entity limit error for views per roadmap',
});

const viewsetsPerProjectMessage = defineMessage({
	id: 'polaris-lib-errors.controllers.entity-limit-error.viewsets-per-project',
	defaultMessage:
		'We couldn’t create a new <item>section</item> because this <item>project</item> has reached its limit for <item>sections</item>. Remove <item>section</item> to create a new <item>section</item>',
	description: 'Flag description for entity limit error for viewsets per project',
});

const viewsetsPerCollectionMessage = defineMessage({
	id: 'polaris-lib-errors.controllers.entity-limit-error.viewsets-per-collection',
	defaultMessage:
		'We couldn’t create a new <item>section</item> because this <item>roadmap</item> has reached its limit for <item>sections</item>. Remove <item>section</item> to create a new <item>section</item>',
	description: 'Flag description for entity limit error for viewsets per roadmap',
});

const viewsetsPerProjectGalaxiaMessage = defineMessage({
	id: 'polaris-lib-errors.controllers.entity-limit-error.viewsets-per-project-galaxia',
	defaultMessage:
		'We couldn’t create a new <item>section</item> because this <item>space</item> has reached its limit for <item>sections</item>. Remove <item>section</item> to create a new <item>section</item>',
	description: 'Flag description for entity limit error for viewsets per space',
});

const messages = {
	[ENTITY_LIMIT_ERROR_TYPE.COMMENTS_PER_VIEW]: commentsPerViewMessage,
	[ENTITY_LIMIT_ERROR_TYPE.FIELDS_PER_VIEW]: fieldsPerViewMessage,
	[ENTITY_LIMIT_ERROR_TYPE.FIELDS_PER_PROJECT]: fieldsPerProjectMessage,
	[ENTITY_LIMIT_ERROR_TYPE.INSIGHTS_PER_IDEA]: insightsPerIdeaMessage,
	[ENTITY_LIMIT_ERROR_TYPE.INSIGHTS_PER_PROJECT]: insightsPerProjectMessage,
	[ENTITY_LIMIT_ERROR_TYPE.LABELS_PER_SNIPPET]: labelsPerSnippetMessage,
	[ENTITY_LIMIT_ERROR_TYPE.OPTIONS_PER_FIELD]: optionsPerFieldMessage,
	[ENTITY_LIMIT_ERROR_TYPE.PLAY_CONTRIBUTIONS_PER_PLAY]: playContributionsPerPlayMessage,
	[ENTITY_LIMIT_ERROR_TYPE.PLAY_CONTRIBUTIONS_PER_PROJECT]: playContributionsPerProjectMessage,
	[ENTITY_LIMIT_ERROR_TYPE.PLAYS_PER_PROJECT]: playsPerProjectMessage,
	[ENTITY_LIMIT_ERROR_TYPE.SNIPPETS_PER_INSIGHT]: snippetsPerInsightMessage,
	[ENTITY_LIMIT_ERROR_TYPE.VIEWS_PER_PROJECT]: viewsPerProjectMessage,
	[ENTITY_LIMIT_ERROR_TYPE.VIEWS_PER_COLLECTION]: viewsPerCollectionMessage,
	[ENTITY_LIMIT_ERROR_TYPE.VIEWSETS_PER_PROJECT]: viewsetsPerProjectMessage,
	[ENTITY_LIMIT_ERROR_TYPE.VIEWSETS_PER_COLLECTION]: viewsetsPerCollectionMessage,
	viewsetsPerProjectGalaxia: viewsetsPerProjectGalaxiaMessage,
} as const;

export class EntityLimitError extends ExtensibleError {
	name = 'EntityLimitError';

	errors: { error: string }[];

	statusCode: number | null;

	type: LimitType;

	constructor(
		message: string,
		errors: { error: string }[] = [],
		statusCode: number | null = null,
		type: LimitType,
	) {
		super(message, { cause: errors[0] });
		this.errors = errors;
		this.statusCode = statusCode;
		this.type = type;
	}
}

const findEntityLimitError = (
	graphQLErrors: ReadonlyArray<GraphQLErrorLike>,
): EntityLimitError | null => {
	const entityLimitError = graphQLErrors.filter((err) => err.extensions?.limitType)[0];
	if (!entityLimitError?.extensions) {
		return null;
	}

	const { statusCode, limitType } = entityLimitError.extensions;
	if (!limitType) {
		return null;
	}
	const entityLimitErrorType = ENTITY_LIMIT_ERROR_TYPE[limitType];

	return new EntityLimitError(
		entityLimitError.message,
		[{ error: entityLimitError.message }],
		statusCode,
		entityLimitErrorType,
	);
};

export const getEntityLimitError = (error: Error | EntityLimitError): EntityLimitError | null => {
	if (error instanceof EntityLimitError) {
		return error;
	}

	return error instanceof RelayNetworkError || error instanceof GraphQLResponseError
		? findEntityLimitError(error.graphQLErrors)
		: null;
};

export const getErrorMessageDescriptor = (error: EntityLimitError): MessageDescriptor | null => {
	if (
		error.type === ENTITY_LIMIT_ERROR_TYPE.VIEWSETS_PER_PROJECT &&
		expVal('project-terminology-refresh', 'isEnabled', false)
	) {
		return messages[`${error.type}Galaxia`];
	}
	return messages[error.type] || null;
};
